import React, { useEffect } from 'react';
import { useQuery } from '@tanstack/react-query';
import { Link } from 'react-router-dom';
import { MapPinIcon } from '@heroicons/react/24/outline';
import { api } from '../lib/api';
import SEO from '../components/SEO';

interface StateInfo {
  state: string;
  count: number;
  slug: string;
}

export default function CampsByCityPage() {
  const { data: states = [], isLoading, error } = useQuery({
    queryKey: ['states'],
    queryFn: () => api.locations.getStates(),
    staleTime: 0, // Always refetch to ensure we get latest data
    cacheTime: 1000 * 60 * 10, // 10 minutes
    refetchOnMount: true, // Always refetch when component mounts
  });

  // Debug logging
  useEffect(() => {
    if (error) {
      console.error('[CampsByCityPage] Error loading states:', error);
    }
    if (isLoading) {
      console.log('[CampsByCityPage] Loading states...');
    }
    if (states.length > 0) {
      console.log(`[CampsByCityPage] ✅ Rendering ${states.length} states:`, states.map(s => `${s.state} (${s.count} camps)`));
      const hasTexas = states.some(s => s.state.toLowerCase().includes('texas'));
      console.log(`[CampsByCityPage] Texas found: ${hasTexas ? 'YES ✅' : 'NO ❌'}`);
    }
  }, [states, isLoading, error]);

  if (isLoading) {
    return (
      <div className="page-container">
        <SEO
          title="Camps by City - Find Summer Camps by Location | CampWhiz.com"
          description="Browse summer camps by state and city. Find camps near you with our organized location-based directory."
          keywords="summer camps by city, camps by state, camp directory, local camps"
          url="/camps-by-city"
        />
        <div className="page-content">
          <div className="text-center">
            <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary mx-auto mb-4"></div>
            <p className="text-gray-600">Loading states...</p>
          </div>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="page-container">
        <SEO
          title="Camps by City - Find Summer Camps by Location | CampWhiz.com"
          description="Browse summer camps by state and city. Find camps near you with our organized location-based directory."
          keywords="summer camps by city, camps by state, camp directory, local camps"
          url="/camps-by-city"
        />
        <div className="page-content">
          <div className="text-center">
            <h1 className="text-3xl font-semibold text-gray-900 mb-4">Error Loading States</h1>
            <p className="text-gray-600 mb-6">There was an error loading the state information. Please try again later.</p>
            <button
              onClick={() => window.location.reload()}
              className="btn btn-primary"
            >
              Retry
            </button>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="page-container">
      <SEO
        title="Camps by City - Find Summer Camps by Location | CampWhiz.com"
        description="Browse summer camps by state and city. Find camps near you with our organized location-based directory."
        keywords="summer camps by city, camps by state, camp directory, local camps"
        url="/camps-by-city"
      />
      
      <div className="page-content">
        {/* Header */}
        <div className="text-center mb-8">
          <h1 className="text-2xl sm:text-3xl md:text-4xl lg:text-5xl font-semibold text-gray-900 mb-4">
            Find Camps by City
          </h1>
          <p className="text-lg text-gray-600 max-w-3xl mx-auto">
            Browse summer camps organized by state and city. Find camps near you or explore new locations.
          </p>
        </div>



        {/* States Grid - Centered */}
        <div className="flex justify-center">
          <div className="flex flex-wrap justify-center gap-6 max-w-6xl w-full">
            {states
              .filter((stateInfo: StateInfo) => stateInfo.state && stateInfo.state.trim() !== '' && stateInfo.slug)
              .map((stateInfo: StateInfo) => (
                <Link
                  key={stateInfo.state}
                  to={`/camps-by-city/${stateInfo.slug}`}
                  className="group bg-white rounded-lg shadow-md hover:shadow-lg transition-all duration-200 p-6 border border-gray-200 hover:border-primary w-full sm:w-[calc(50%-0.75rem)] lg:w-[calc(33.333%-1rem)] xl:w-[calc(25%-1.125rem)]"
                >
                  <div className="flex items-center justify-between mb-4">
                    <MapPinIcon className="h-8 w-8 text-primary group-hover:text-primary/80 transition-colors" />
                    <span className="text-sm font-medium text-primary bg-primary/10 px-2 py-1 rounded-full">
                      {stateInfo.count} camp{stateInfo.count !== 1 ? 's' : ''}
                    </span>
                  </div>
                  <h2 className="text-xl font-semibold text-gray-900 group-hover:text-primary transition-colors">
                    {stateInfo.state}
                  </h2>
                </Link>
              ))}
          </div>
        </div>
      </div>
    </div>
  );
}
